import { useEffect, useState } from "react";
import type { AbstractAgent, Message } from "@ag-ui/client";

// Tracks live AG-UI run_id (== backend trace_id == turn_id) associations for
// each user message that triggered a run during this session.
//
// History-loaded user messages carry `turnId` in their AG-UI extras (see
// analysis-agent/app/agui/history.py). Newly-submitted user messages added
// client-side don't — `RunStartedEvent` fires once the server starts
// processing them, and the runId on that event is the canonical turn id.
//
// Returns a Map<userMessageId, turnId> consumed by useTurns to attach the
// turn id to the assistant turn that follows the user message.
export function useStreamingTurnIds(agent: AbstractAgent): ReadonlyMap<string, string> {
  const [map, setMap] = useState<Map<string, string>>(() => new Map());

  useEffect(() => {
    const sub = agent.subscribe({
      onRunStartedEvent: ({ event }) => {
        const messages = agent.messages as ReadonlyArray<Message>;
        // Find the most recent user message — that's the one that triggered
        // this run.
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].role === "user") {
            const userId = messages[i].id;
            setMap((prev) => {
              if (prev.get(userId) === event.runId) return prev;
              const next = new Map(prev);
              next.set(userId, event.runId);
              return next;
            });
            return;
          }
        }
      },
    });
    return () => sub.unsubscribe();
  }, [agent]);

  return map;
}
